/**
 * Vendas reais no Painel (modo ao vivo). Envolve o provider da simulação e troca tudo o que é venda
 * pelos pedidos reais da Kiwify que o servidor guarda (`GET /api/painel/vendas`):
 *
 * - `getSnapshot`: faturamento (valor dos produtos), comissão (`revenueNet`), vendas do curso e do
 *   20X, ticket, juros, Pix/boleto pendentes, reembolsos; a frase do dia é refeita com os números reais.
 * - `getSeries`: faturamento por hora/dia (e acumulado) e vendas por hora/dia, com filtro de origem.
 * - `list('orders' | 'refunds' | 'activity')`: pedidos reais (sem dado pessoal).
 * - `subscribe`: toda mensagem da simulação (menos o "refaça tudo") vira atualização com os números
 *   reais (mesmo `seq`, para a ordem do stream não quebrar); venda real nova vira `order.upsert`.
 *
 * Política do modo ao vivo: nenhum número da simulação aparece como real. O snapshot, os KPIs da V2,
 * o detalhe da métrica, as séries e o `kpi.patch` levam só o que foi calculado de verdade (núcleo e
 * módulos); o resto sai sem valor, com o motivo (`NO_REAL_SOURCE`), ou com o gráfico vazio.
 *
 * Este arquivo é o núcleo: os outros assuntos ficam em módulos próprios (`RealModule`, em
 * `realCore.ts`), compostos nesta ordem: mídia, VSL, ajustes, financeiro, plano, funil, backend,
 * decisões, recuperação e reembolsos por coorte. `patchSnapshot`/`patchKpisV2` rodam depois do núcleo; `series`, `list` e
 * `kpiDetail` perguntam aos módulos antes do núcleo.
 */

import type {
  ColumnarSeries,
  DashboardSnapshot,
  DataProvider,
  FunnelStep,
  IntegrationStatus,
  KpiId,
  KpiIdV2,
  KpiValue,
  KpiValueV2,
  ListKind,
  ListQuery,
  LiveMessage,
  Order,
  Page,
  PainelProviderV2,
  Query,
  Refund,
  SeriesQuery,
  Settings,
  TestLaneItem,
} from '@rota/contracts';
import {
  CARD_MIN_SAMPLE,
  type CardStats,
  courseCardStats,
  coursePixStats,
  OPEN_LOOKBACK_MS,
} from '@/lib/salesRules';
import { isDefaultLiveQuery } from '../defaultLiveQuery';
import { automationModule } from './realAutomation';
import { backendModule } from './realBackend';
import {
  BRT,
  buckets,
  CACHE_MS,
  compareMs,
  coverageLabel,
  coverageStart,
  createOrdersSource,
  cumulative,
  DAY,
  dayStart,
  type FetchOrders,
  fetchRealOrders,
  finalOf,
  freshness,
  HOUR,
  isOpenRow,
  isPaid,
  isRefundLike,
  isSold,
  kv,
  type LiveOrder,
  linkUpsellParents,
  matches,
  metaFresh,
  noRealKpi,
  paginate,
  pixMedianMinutes,
  type RealCtx,
  type RealKpisResult,
  type RealModule,
  type RealOrderRow,
  rangeMs,
  rate,
  revenueOf,
  setKpiV2,
  silenceWarnFrom,
  sparkSize,
  type Totals,
  todayKey,
  toOrder,
  totals,
} from './realCore';
import { decisionsModule, realActivity } from './realDecisions';
import { financeModule } from './realFinance';
import { funnelModule, TRACKER_KPIS } from './realFunnel';
import {
  createMediaSource,
  type FetchMedia,
  fetchRealMedia,
  type MediaGap,
  mediaCostOf,
  mediaGap,
  mediaModule,
  metaFreshness,
  metaIntegrationStatus,
  type RealMedia,
} from './realMedia';
import { planModule } from './realPlan';
import { recoveryModule } from './realRecovery';
import { refundsModule } from './realRefunds';
import { createSettingsState, settingsModule } from './realSettings';
import { type FetchInsights, fetchRealInsights } from './realTraffic';
import { createVslSource, type RealVsl, VSL_KPI_IDS, vslModule, vturbStatus } from './realVsl';

// O que já saía daqui continua saindo (os imports antigos seguem valendo).
export {
  compareMs,
  type FetchOrders,
  fetchRealOrders,
  type RealOrderRow,
  type RealOrdersResult,
  rangeMs,
  revenueOf,
  toOrder,
} from './realCore';
export { type FetchMedia, fetchRealMedia, type RealMedia } from './realMedia';

/** KPIs de pedidos: sempre reais (no stream, o número simulado nunca passa). */
const ORDER_KPIS: KpiId[] = [
  'revenueGross',
  'revenueNet',
  'frontSales',
  'upsellSales',
  'aov',
  'interestPaid',
  'pendingRevenue',
  'refundsAmount',
  'refundsCount',
  'upsellTakeRate',
  'cardApprovalAttempt',
  'cardApprovalBuyer',
  'pixPaidRate',
  'cartsAbandoned',
  'revenueFinal',
  'pixMedianMin',
];

/**
 * KPIs que só existem pelo webhook da Kiwify (abandono, Pix gerado, recusa): a exportação importada,
 * que cobre os dias antes do 1º aviso, não tem esses eventos. Comparar com esses dias não vale.
 */
const WEBHOOK_ONLY_KPIS: KpiId[] = [
  'cartsAbandoned',
  'pixPaidRate',
  'cardApprovalAttempt',
  'cardApprovalBuyer',
  'pixMedianMin',
];

/**
 * Motivo de um número sem fonte real no modo ao vivo. A simulação nunca aparece como real: o que não
 * é calculado com os pedidos, a Meta, a VTurb ou o servidor fica sem valor, com este motivo.
 */
export const NO_REAL_SOURCE = 'Não medido: ainda não há fonte real ligada para este número';

/** Nota do "Pendente agora": um estoque (de qualquer dia), não um número do período. */
export const PENDING_STOCK_NOTE = 'Pix e boletos abertos agora, de qualquer dia';

/**
 * Nota do pendente de um período passado: a soma da aba Pendentes desse período (pedidos aguardando
 * pagamento que ainda não venceram; o Pix vencido sai, e a aba diz quantos ficaram de fora).
 */
export const PENDING_PAST_NOTE = 'Pedidos do período ainda aguardando pagamento (sem os Pix vencidos)';

/**
 * Pendente de um período passado: o valor dos produtos de cada pedido aguardando pagamento do período,
 * sem os carrinhos abandonados nem os Pix vencidos até `at` (ou trocados por outro pedido pago). É
 * exatamente o que a aba Pendentes lista nesse período (`stillPending`).
 */
export function waitingInPeriod(list: readonly Order[], at: number): number {
  let cents = 0;
  for (const o of list)
    if (
      o.status === 'waiting_payment' &&
      !(o as LiveOrder).abandoned &&
      !(o.pixExpiresAt != null && o.pixExpiresAt <= at)
    )
      cents += o.amounts.gross;
  return cents;
}

/** "25/10 às 14:14" no fuso de São Paulo. */
const dayTime = (at: number): string => {
  const d = new Date(at - BRT).toISOString();
  return `${d.slice(8, 10)}/${d.slice(5, 7)} às ${d.slice(11, 16)}`;
};

/** "14:14" no fuso de São Paulo. */
const clockTime = (at: number): string => new Date(at - BRT).toISOString().slice(11, 16);

/** Nota dos números de vendas quando a leitura dos pedidos falha: o último dado real, parado. */
export const salesDownNote = (at: number): string =>
  `Vendas indisponíveis (erro no servidor) · último dado às ${clockTime(at)}`;

/** Nota dos números de vendas quando a leitura dos pedidos falha sem nenhuma leitura boa antes. */
export const SALES_DOWN_NOTE = 'Vendas indisponíveis (erro no servidor): ainda sem dado para este período';

/** Leituras de pedidos que falham em seguida (uma por atualização de 30 s) até a Kiwify virar "com erro". */
export const KIWIFY_FAILS_FOR_ERROR = 2;

/** Números de hoje sem atualizar há mais que isto (a atualização é a cada 30 s): parados. */
export const LIVE_KPIS_STALE_MS = 2 * 60_000;

/** Dias de pedidos para o limite de silêncio da Kiwify quando o servidor não manda (o mesmo do servidor). */
const SILENCE_DAYS = 7;

/** Aba escondida por mais que isto: na volta, relê tudo na hora. */
export const BACK_FROM_HIDDEN_MS = 60_000;

/**
 * KPIs que não usam os pedidos da Kiwify (só a Meta, a VTurb, o rastreador ou a meta de gasto): seguem
 * valendo, com o frescor da própria fonte, quando a leitura dos pedidos falha. A conversão da página
 * da VSL (vendas ÷ visualizações) usa os pedidos e fica de fora.
 */
const NOT_FROM_ORDERS: ReadonlySet<KpiId> = new Set<KpiId>([
  'spendAm',
  'mediaCost',
  'impressions',
  'cpm',
  'linkClicks',
  'ctrLink',
  'cpcLink',
  'lpv',
  'connectRate',
  'cpa',
  'roas',
  'roi',
  'goalSpendDay',
  ...VSL_KPI_IDS.filter((id) => id !== 'vslPageConv'),
  ...TRACKER_KPIS,
]);

/**
 * Marca como parados (frescor da Kiwify `stale`, com a hora da última leitura boa) os KPIs que dependem
 * dos pedidos; os que não usam os pedidos (`NOT_FROM_ORDERS`) ficam como estão. `note` troca a nota (ex.: "Vendas indisponíveis...").
 */
export function markSalesStale(
  kpis: Partial<Record<KpiId, KpiValue>>,
  asOf: number,
  now: number,
  note?: string,
): Partial<Record<KpiId, KpiValue>> {
  const out: Partial<Record<KpiId, KpiValue>> = {};
  for (const [id, v] of Object.entries(kpis) as [KpiId, KpiValue | undefined][]) {
    if (!v) continue;
    if (NOT_FROM_ORDERS.has(id) || v.value == null) {
      out[id] = v;
      continue;
    }
    const kiwi = {
      source: 'kiwify' as const,
      latency: 'realtime' as const,
      fetchedAt: now,
      dataAsOf: asOf,
      stale: true,
    };
    out[id] = {
      ...v,
      freshness: [...(v.freshness ?? []).filter((f) => f.source !== 'kiwify'), kiwi],
      ...(note ? { note } : {}),
    };
  }
  return out;
}

/**
 * Leitura dos pedidos falhou sem nenhuma leitura boa antes: os KPIs que dependem dos pedidos ficam
 * sem valor (nunca zero nem o número da simulação), com a nota e a Kiwify parada; os que não usam os
 * pedidos (`NOT_FROM_ORDERS`: gasto, CPA e ROAS da Meta…) seguem com o dado real.
 */
export function markSalesDown(
  kpis: Partial<Record<KpiId, KpiValue>>,
  now: number,
  asOf: number = now,
): Partial<Record<KpiId, KpiValue>> {
  const out: Partial<Record<KpiId, KpiValue>> = {};
  for (const [id, v] of Object.entries(kpis) as [KpiId, KpiValue | undefined][]) {
    if (!v) continue;
    if (NOT_FROM_ORDERS.has(id)) {
      out[id] = v;
      continue;
    }
    const kiwi = {
      source: 'kiwify' as const,
      latency: 'realtime' as const,
      fetchedAt: now,
      dataAsOf: asOf,
      stale: true,
    };
    out[id] = {
      ...v,
      value: null,
      compareValue: null,
      delta: null,
      spark: [],
      status: 'neutral',
      note: SALES_DOWN_NOTE,
      freshness: [...(v.freshness ?? []).filter((f) => f.source !== 'kiwify'), kiwi],
    } as KpiValue;
  }
  return out;
}

/**
 * Métricas de pedidos com série real por dia/hora (o detalhe da métrica não cai na simulação). As
 * vendas contam as aprovadas, inclusive as reembolsadas depois (a regra da tela Vendas), e o
 * faturamento também; comissão e juros só as pagas.
 */
const ORDER_KPI_PICK: Partial<Record<string, (o: RealOrderRow) => number>> = {
  frontSales: (o) => (isSold(o) && o.kind === 'front' ? 1 : 0),
  upsellSales: (o) => (isSold(o) && o.kind === 'upsell' ? 1 : 0),
  revenueGross: revenueOf,
  revenueNet: (o) => (isPaid(o) ? (o.commissionCents ?? 0) : 0),
  interestPaid: (o) => (isPaid(o) ? Math.max(0, o.grossCents - (o.baseCents ?? o.grossCents)) : 0),
};

/**
 * Extensão do provider ao vivo (fora do contrato): o 1º aviso do webhook da Kiwify. Antes dele o painel
 * só tem a exportação (sem abandonos, Pix gerados nem recusas): o seletor de período avisa com ele.
 */
export interface KiwifyWebhookInfo {
  kiwifyWebhookSince(): Promise<number | null>;
}

/** 1º aviso do webhook da Kiwify; null fora do modo ao vivo (simulação) ou sem a informação. */
export async function kiwifyWebhookSince(p: DataProvider): Promise<number | null> {
  const f = (p as Partial<KiwifyWebhookInfo>).kiwifyWebhookSince;
  return f ? f().catch(() => null) : null;
}

const brl = (cents: number) =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/**
 * Frase do dia pela origem de cada número: pedidos pagos de todos os produtos, separados em curso,
 * 20X e consulta Premium (nunca "vendas" para tudo). Conta os mesmos pedidos que o cartão "Vendas do
 * curso" e o total "pedidos aprovados" da lista. As vendas do período reembolsadas depois entram no
 * faturamento (o reembolso sai pela data dele): a frase diz quantas são, para a conta fechar.
 */
export function salesHeadline(t: Totals): string {
  const later = t.sold - t.paid;
  if (t.paid === 0 && later === 0) return 'Nenhum pedido pago no período';
  const parts: string[] = [];
  if (t.front) parts.push(`${t.front} do curso`);
  if (t.upsell) parts.push(`${t.upsell} do 20X`);
  if (t.premium) parts.push(`${t.premium} ${t.premium === 1 ? 'consulta Premium' : 'consultas Premium'}`);
  const other = t.paid - t.front - t.upsell - t.premium;
  if (other > 0) parts.push(`${other} de outro produto`);
  const n = `${t.paid} ${t.paid === 1 ? 'pedido pago' : 'pedidos pagos'}`;
  const split =
    t.paid === 0 ? '' : t.front === t.paid ? ' do curso' : parts.length > 0 ? ` (${parts.join(' + ')})` : '';
  const refunded = later > 0 ? ` e ${later} ${later === 1 ? 'reembolsado' : 'reembolsados'} depois` : '';
  return `${n}${split}${refunded} · ${brl(t.gross)} de faturamento · ${brl(t.commission)} de comissão`;
}

/** Razão balde a balde (null onde o denominador é zero ou o balde ainda não chegou). */
const ratioOf = (num: (number | null)[], den: (number | null)[]): (number | null)[] =>
  num.map((n, i) => {
    const d = den[i];
    return n == null || d == null || !d ? null : n / d;
  });

/**
 * Taxas da tela Vendas com série real: o 20X sobre o curso e o ticket (valor dos produtos ÷ compradores
 * do curso). As duas contam os mesmos compradores do curso: os pedidos pagos (como o cartão "Vendas do
 * curso"). Por hora, o acumulado do dia (a ponta é o número do topo); por dia, a razão de cada dia.
 */
const RATIO_SERIES: Partial<
  Record<KpiId, { num: (o: RealOrderRow) => number; den: (o: RealOrderRow) => number; cents?: true }>
> = {
  upsellTakeRate: {
    num: (o) => (isPaid(o) && o.kind === 'upsell' ? 1 : 0),
    den: (o) => (isPaid(o) && o.kind === 'front' ? 1 : 0),
  },
  aov: {
    num: (o) => (isPaid(o) ? revenueOf(o) : 0),
    den: (o) => (isPaid(o) && o.kind === 'front' ? 1 : 0),
    cents: true,
  },
};

/** Aprovação de cartão do curso: abaixo de 10 na amostra, sem julgamento (o VEN-03 diz o tamanho). */
function cardKpi(
  id: 'cardApprovalAttempt' | 'cardApprovalBuyer',
  s: CardStats,
  c: CardStats | null,
  now: number,
): KpiValue {
  const pick = (x: CardStats) =>
    id === 'cardApprovalAttempt' ? rate(x.approved, x.attempts) : rate(x.approvedBuyers, x.buyers);
  const n = id === 'cardApprovalAttempt' ? s.attempts : s.buyers;
  return {
    ...kv(id, pick(s), c ? pick(c) : null, now),
    status: n < CARD_MIN_SAMPLE ? 'insufficient' : 'neutral',
    sample: { n, min: CARD_MIN_SAMPLE, unit: id === 'cardApprovalAttempt' ? 'tentativas' : 'compradores' },
  };
}

/** Gráfico sem conta real: vazio (a tela mostra o estado vazio), nunca a série da simulação. */
const emptySeries = (): ColumnarSeries => ({ t: [], series: [], markers: [], freshness: [] });

/**
 * Política do modo ao vivo: um KPI que ainda é o próprio objeto da simulação (ninguém o calculou com
 * os pedidos, a Meta, a VTurb ou o servidor) sai sem valor, com o motivo. Os reais ficam como estão.
 */
function onlyReal<K extends string, V extends { id: unknown }>(
  out: Partial<Record<K, V>>,
  sim: Partial<Record<K, V>>,
): Partial<Record<K, V>> {
  const res: Partial<Record<K, V>> = {};
  for (const id of Object.keys(out) as K[]) {
    const v = out[id];
    if (!v) continue;
    res[id] = v === sim[id] ? (noRealKpi(id as never, NO_REAL_SOURCE) as unknown as V) : v;
  }
  return res;
}

/** Canais do orgânico (bio, direct da automação, outros links); sem origem nunca é orgânico. */
const ORGANIC_LABEL: Partial<Record<RealOrderRow['channel'], string>> = {
  ig_bio: 'bio',
  automacao: 'direct',
  outro: 'outro',
};

/**
 * Vendas do curso do orgânico (a mesma regra do canal "Orgânico" do Hoje e do Funil) e a parcela sem
 * origem, pelas vendas aprovadas do período e do recorte (inclusive as reembolsadas depois, como a
 * tela Vendas).
 */
export function channelKpis(
  rows: readonly RealOrderRow[],
  q: Query,
): { organicSales: number; organicNote: string; unattributedShare: number | null } {
  const fronts = rows.filter((o) => isSold(o) && matches(o, q) && o.kind === 'front');
  const by = new Map<string, number>();
  let organic = 0;
  for (const o of fronts) {
    const label = ORGANIC_LABEL[o.channel];
    if (!label) continue;
    organic += 1;
    by.set(label, (by.get(label) ?? 0) + 1);
  }
  return {
    organicSales: organic,
    organicNote: ['bio', 'direct', 'outro'].map((k) => `${k} ${by.get(k) ?? 0}`).join(' · '),
    unattributedShare: fronts.length
      ? fronts.filter((o) => o.channel === 'sem_origem').length / fronts.length
      : null,
  };
}

const ordersOf = (rows: readonly RealOrderRow[]): LiveOrder[] =>
  rows.map(toOrder).filter((o): o is LiveOrder => o != null);

/**
 * O recorte ao vivo padrão (Hoje, sem filtro, contra ontem no mesmo horário, sem Replay): o mesmo que
 * o `refresh` do stream calcula. Só ele grava os números de hoje que o `kpi.patch` leva.
 */
export { isDefaultLiveQuery };

export function withRealSales(
  provider: DataProvider,
  fetchOrders: FetchOrders = fetchRealOrders,
  clock: () => number = Date.now,
  fetchMedia: FetchMedia = fetchRealMedia,
  fetchInsights: FetchInsights = fetchRealInsights,
): DataProvider {
  const v2 = provider as PainelProviderV2;
  // Janela larga só com o servidor de verdade (que filtra pelo período); os dublês dos testes devolvem
  // pedidos pelo começo pedido e testam o resto da lógica sem ela.
  const orders = createOrdersSource(fetchOrders, clock, fetchOrders === fetchRealOrders ? 100 : 0);
  const media = createMediaSource(fetchMedia, fetchInsights, clock);
  const vsl = createVslSource(clock);
  const settings = createSettingsState();
  const load = orders.load;

  const ctx: RealCtx = {
    provider,
    v2,
    clock,
    load,
    loadRefunds: orders.loadRefunds,
    webhookSince: orders.webhookSince,
    dataStart: orders.dataStart,
    invalidateOrders: orders.invalidate,
    lastEventAt: orders.lastEventAt,
    loadMedia: media.loadMedia,
    loadInsights: media.loadInsights,
    invalidateMedia: media.invalidate,
    loadVsl: vsl.load,
    lastVsl: vsl.last,
    costOf: (x) => mediaCostOf(x, settings.metaTaxPct()),
    metaTaxPct: settings.metaTaxPct,
    settings: settings.settings,
    realKpis: (q) => realKpis(q),
    metaFresh,
    kiwiFresh: (now) => freshness(now, orders.lastEventAt() ?? now),
  };

  // Ordem de composição (a mesma de `patchSnapshot`, `series`, `list` e `kpiDetail`).
  const modules: RealModule[] = [
    mediaModule(ctx),
    vslModule(ctx),
    settingsModule(ctx, settings),
    financeModule(ctx),
    planModule(ctx),
    funnelModule(ctx),
    backendModule(ctx),
    decisionsModule(ctx),
    recoveryModule(ctx),
    refundsModule(ctx),
    automationModule(ctx),
  ];
  /** KPIs que o detalhe da métrica pergunta ao `realKpis` (o resto sai do snapshot ao vivo). */
  const realKpiIds = new Set<KpiId>([...ORDER_KPIS, ...modules.flatMap((m) => m.kpiIds ?? [])]);

  /**
   * Situação real da Kiwify (último aviso recebido), nunca a simulada. O limite de silêncio vem do
   * servidor (ritmo real dos eventos naquele horário); servidor antigo: o mesmo cálculo com os pedidos
   * dos últimos 7 dias, como o servidor. Passou do limite: "atrasada" (amarelo). Leituras de pedidos
   * falhando em seguida (`failing`): "com erro", com a hora do último aviso real e a mensagem.
   */
  async function kiwifyStatus(failing = false): Promise<IntegrationStatus> {
    const now = clock();
    const to = Math.ceil(now / 60_000) * 60_000;
    // Começo fixo no dia (0h de ontem ou de 7 dias atrás): não muda a cada minuto, então não enche o
    // cache de leituras nem a última leitura boa de cada começo.
    const fromServer = orders.silenceWarnMs();
    // A 1ª leitura (antes de saber se o servidor manda o limite) já pega os 7 dias.
    const from = dayStart(todayKey(now)) - (fromServer == null ? SILENCE_DAYS : 1) * DAY;
    const rows = await load(from, to).catch(() => null);
    // O servidor mede pelos avisos do webhook: antes do 1º aviso só há a exportação (pausas longas
    // que não são silêncio da Kiwify), então esses dias ficam fora da conta.
    const since = orders.webhookSince() ?? 0;
    const sla =
      orders.silenceWarnMs() ??
      silenceWarnFrom(rows?.filter((o) => o.at >= since).map((o) => o.at) ?? [], now);
    const recent = rows?.filter((o) => o.at > now - DAY) ?? null;
    const last = orders.lastEventAt();
    const lag = last == null ? null : Math.max(0, now - last);
    const base = {
      source: 'kiwify' as const,
      label: 'Kiwify · webhooks',
      latency: 'realtime' as const,
      lastSyncAt: last,
      lagMs: lag,
      slaMs: sla,
      eventsLastHour: recent ? recent.filter((o) => o.at >= now - HOUR).length : null,
    };
    if (failing) {
      const h = orders.health();
      return {
        ...base,
        state: 'error',
        lastError: {
          at: h.lastFailAt ?? now,
          code: 'source_unavailable',
          message: `Vendas indisponíveis (erro no servidor: ${h.lastError ?? 'sem resposta'})`,
        },
      };
    }
    return { ...base, state: lag == null || lag > sla ? 'degraded' : 'connected' };
  }
  const todayStart = () => dayStart(todayKey(clock()));

  /**
   * Pedidos que a Recuperação deu como perdidos: saem do pendente (como na aba Pendentes). Cache
   * curto: o stream refaz os números a cada 30 s e não precisa reler a fila a cada vez.
   */
  let lostCache: { at: number; p: Promise<Set<string>> } | null = null;
  function lostOrderIds(): Promise<Set<string>> {
    const now = clock();
    if (lostCache && now - lostCache.at < CACHE_MS) return lostCache.p;
    const p = readLostOrderIds();
    lostCache = { at: now, p };
    return p;
  }
  async function readLostOrderIds(): Promise<Set<string>> {
    const out = new Set<string>();
    const today = todayKey(clock());
    const lq: ListQuery = {
      range: { preset: 'custom', from: today, to: today },
      compare: 'none',
      filters: {},
      limit: 200,
      status: ['lost'],
    };
    for (const m of modules) {
      const page = await m.list?.('recovery', lq)?.catch(() => undefined);
      if (!page) continue;
      for (const r of page.rows as { orderId?: string | null; status?: string }[])
        if (r.orderId && r.status === 'lost') out.add(r.orderId);
      break;
    }
    return out;
  }

  /**
   * "Pendente agora" (VEN-01, Hoje): Pix no prazo e boletos de até 3 dias abertos neste instante, de
   * qualquer dia dos últimos 4, sem os dados como perdidos na Recuperação. É a soma da aba Pendentes
   * (mesma janela, mesma regra `isOpenNow`). Período que termina antes de hoje: null (vale o período).
   */
  async function pendingStock(q: Query, now: number): Promise<{ cents: number; count: number } | null> {
    if (q.range.to < todayKey(now)) return null;
    const from = dayStart(todayKey(now - OPEN_LOOKBACK_MS));
    const to = Math.ceil(now / 60_000) * 60_000;
    const [rows, lost] = await Promise.all([load(from, to), lostOrderIds()]);
    let cents = 0;
    let count = 0;
    for (const o of rows) {
      if (!matches(o, q) || lost.has(o.id) || !isOpenRow(o, now)) continue;
      cents += revenueOf(o);
      count += 1;
    }
    return { cents, count };
  }

  /** Status sem o que só a simulação tem (contas, cota, minigráfico e erro inventados). */
  const cleanSim = (s: IntegrationStatus | undefined): IntegrationStatus | undefined => {
    if (!s) return undefined;
    const { accounts: _a, quota: _q, eventsSpark: _e, lastError: _l, ...rest } = s;
    return rest;
  };
  // Última situação real de cada fonte: o stream troca a simulada por ela, e um erro mantém a hora da
  // última sincronização boa (nunca a da simulação).
  let kiwifyLive: IntegrationStatus | null = null;
  let metaLive: IntegrationStatus | null = null;
  let vturbLive: IntegrationStatus | null = null;
  const realMeta = (s: IntegrationStatus, m: RealMedia | null, gap: MediaGap | null, now: number) => {
    const st = metaIntegrationStatus(s, m, gap, now);
    const prevAt = metaLive?.lastSyncAt ?? null;
    metaLive =
      st.state === 'error' && st.lastSyncAt == null && prevAt != null
        ? { ...st, lastSyncAt: prevAt, lagMs: Math.max(0, now - prevAt) }
        : st;
    return metaLive;
  };
  const realVturb = (v: RealVsl | null, s: IntegrationStatus | undefined, now: number) => {
    const st = vturbStatus(v, cleanSim(s), now);
    const prevAt = vturbLive?.lastSyncAt ?? null;
    vturbLive =
      st.state === 'error' && st.lastSyncAt == null && prevAt != null
        ? { ...st, lastSyncAt: prevAt, lagMs: Math.max(0, now - prevAt) }
        : st;
    return vturbLive;
  };

  /**
   * Situação das fontes (rodapé do menu, selo "Ao vivo"): Kiwify pelo último aviso real, Meta pela
   * última sincronização real (com erro ou não conectada quando for o caso) e VTurb/rastreador pela
   * leitura real ou "não configurado" (nada de "ao vivo" simulado).
   */
  const withRealKiwify = async (list: IntegrationStatus[]): Promise<IntegrationStatus[]> => {
    const now = clock();
    const from = now - HOUR;
    const [real, m, v] = await Promise.all([
      kiwifyStatus(),
      ctx.loadMedia(from, now).catch(() => null),
      ctx.loadVsl(todayStart(), now),
    ]);
    kiwifyLive = real;
    const out = list.map((s): IntegrationStatus => {
      if (s.source === 'kiwify') return real;
      if (s.source === 'meta') return realMeta(s, m, mediaGap(ctx.loadMedia, from, now), now);
      if (s.source === 'vturb') return realVturb(v, s, now);
      return {
        ...(cleanSim(s) as IntegrationStatus),
        state: 'not_configured',
        lastSyncAt: null,
        lagMs: null,
        eventsLastHour: null,
      };
    });
    return list.some((s) => s.source === 'kiwify') ? out : [...out, real];
  };
  // Hoje é o período ao vivo: o último snapshot real do dia alimenta as mensagens do stream.
  let todayKpis: Partial<Record<KpiId, KpiValue>> | null = null;
  /** Quando os números de hoje foram lidos de verdade (sem falha) pela última vez. */
  let todayKpisAt: number | null = null;

  async function realKpis(q: Query): Promise<RealKpisResult> {
    const now = clock();
    const r = rangeMs(q, now);
    const cr = compareMs(q, now);
    // "Pendente agora": estoque aberto de qualquer dia (a mesma regra e a mesma soma da aba Pendentes).
    // Sem leitura boa nenhuma (1ª carga, período ou comparação novos): o período sai como "vendas
    // indisponíveis" (sem valor) e a Meta continua; a comparação que falha só some.
    let stockErr: unknown;
    const [got, cres, stock] = await Promise.all([
      orders.loadFull(r.from, r.to).catch(() => null),
      cr ? orders.loadFull(cr.from, cr.to).catch(() => null) : Promise.resolve(null),
      pendingStock(q, now).catch((e: unknown) => {
        stockErr = e;
        return null;
      }),
    ]);
    const down = got == null;
    // O estoque falhou com os pedidos do período lidos: erro de verdade (não vira "pendente do período").
    if (!down && stockErr !== undefined) throw stockErr;
    const res: Awaited<ReturnType<typeof orders.loadFull>> = got ?? { orders: [], lastEventAt: null };
    // Antes do 1º pedido da base não há dado: os baldes ficam vazios (nunca "R$ 0") e uma comparação
    // que começa antes disso some (contra uma soma cortada ou zerada, o delta engana).
    const cov = coverageStart(res.dataStartMs ?? null) ?? orders.dataStart();
    const noBase = cr != null && cov != null && cr.from < cov;
    const crx = noBase ? null : cr;
    const cur = res.orders.filter((o) => matches(o, q));
    const cmp = noBase ? null : (cres?.orders.filter((o) => matches(o, q)) ?? null);
    // Reembolsos pela data do reembolso (lista própria do servidor; servidor antigo: pela compra).
    const t = totals(cur, res.refunds?.filter((o) => matches(o, q)) ?? null);
    const c = cmp ? totals(cmp, cres?.refunds?.filter((o) => matches(o, q)) ?? null) : null;
    const size = sparkSize(r);
    const paid = cur.filter(isPaid);
    const paidCmp = cmp?.filter(isPaid) ?? [];
    // Faturamento: vendas aprovadas no período, inclusive as reembolsadas depois (como `totals`).
    const sold = cur.filter(isSold);
    const soldCmp = cmp?.filter(isSold) ?? [];
    const sp = (pick: (o: RealOrderRow) => number, list = paid) =>
      buckets(list, r.from, r.end, size, now, pick, cov).values;
    const spc = (pick: (o: RealOrderRow) => number, list = paidCmp) =>
      crx ? buckets(list, crx.from, crx.from + (r.end - r.from), size, crx.to, pick).values : undefined;
    const grossS = sp(revenueOf, sold);
    // Conversão do 20X acumulada no dia (20X ÷ curso até cada hora), na hoje e na comparação.
    const frontS = sp((o) => (o.kind === 'front' ? 1 : 0));
    // Ticket: valor dos produtos pagos acumulado ÷ compradores do curso acumulados (os mesmos da
    // conversão do 20X); a ponta é o número do topo.
    const aovS = ratioOf(cumulative(sp(revenueOf)), cumulative(frontS)).map((v) =>
      v == null ? null : Math.round(v),
    );
    const upsellS = sp((o) => (o.kind === 'upsell' ? 1 : 0));
    const frontC = spc((o) => (o.kind === 'front' ? 1 : 0));
    const upsellC = spc((o) => (o.kind === 'upsell' ? 1 : 0));
    // Contagem de vendas: as aprovadas, inclusive as reembolsadas depois (a regra da tela Vendas, uma
    // só em todas as telas); as reembolsadas também vão à parte ("N reembolsadas", na série por hora).
    const isFront = (o: RealOrderRow) => (o.kind === 'front' ? 1 : 0);
    const isUpsell = (o: RealOrderRow) => (o.kind === 'upsell' ? 1 : 0);
    const takeS = ratioOf(cumulative(upsellS), cumulative(frontS));
    const takeC = frontC && upsellC ? ratioOf(cumulative(upsellC), cumulative(frontC)) : undefined;
    // Cartão e Pix do curso pela data de criação: a mesma conta da lista do VEN-03 (`@/lib/salesRules`).
    const win = { from: r.from, to: r.to };
    const curOrders = ordersOf(cur);
    const cmpOrders = cmp && crx ? ordersOf(cmp) : null;
    const card = courseCardStats(curOrders, win);
    const cardC = cmpOrders && crx ? courseCardStats(cmpOrders, crx) : null;
    const pix = coursePixStats(curOrders, win);
    const pixC = cmpOrders && crx ? coursePixStats(cmpOrders, crx) : null;
    const kpis: Partial<Record<KpiId, KpiValue>> = {
      revenueGross: kv('revenueGross', t.gross, c?.gross ?? null, now, grossS, spc(revenueOf, soldCmp)),
      revenueNet: kv(
        'revenueNet',
        t.commission,
        c?.commission ?? null,
        now,
        sp((o) => o.commissionCents ?? 0),
        spc((o) => o.commissionCents ?? 0),
      ),
      frontSales: kv(
        'frontSales',
        t.frontSold,
        c?.frontSold ?? null,
        now,
        sp(isFront, sold),
        spc(isFront, soldCmp),
      ),
      upsellSales: kv(
        'upsellSales',
        t.upsellSold,
        c?.upsellSold ?? null,
        now,
        sp(isUpsell, sold),
        spc(isUpsell, soldCmp),
      ),
      // Ticket: valor dos produtos dos pedidos pagos ÷ compradores do curso (pagos, como o cartão
      // "Vendas do curso" e a conversão do 20X: um denominador só para "compradores do curso").
      aov: kv(
        'aov',
        t.front ? Math.round(t.paidRevenue / t.front) : null,
        c?.front ? Math.round(c.paidRevenue / c.front) : null,
        now,
        aovS,
      ),
      interestPaid: kv('interestPaid', t.interest, c?.interest ?? null, now),
      // Período que chega até agora: o estoque aberto (sem comparação: estoque não é do período).
      // Período passado: o que ficou aguardando naquele período, a mesma soma da aba Pendentes dele.
      pendingRevenue: stock
        ? { ...kv('pendingRevenue', stock.cents, null, now), note: PENDING_STOCK_NOTE }
        : {
            ...kv(
              'pendingRevenue',
              waitingInPeriod(curOrders, now),
              cmpOrders ? waitingInPeriod(cmpOrders, now) : null,
              now,
            ),
            note: PENDING_PAST_NOTE,
          },
      refundsAmount: kv('refundsAmount', t.refundsGross, c?.refundsGross ?? null, now),
      refundsCount: kv('refundsCount', t.refunds, c?.refunds ?? null, now),
      upsellTakeRate: kv(
        'upsellTakeRate',
        t.front ? t.upsell / t.front : null,
        c?.front ? c.upsell / c.front : null,
        now,
        takeS,
        takeC,
      ),
      // Só o curso, como o mix, os motivos de recusa e a amostra do VEN-03 (o 20X nunca entra junto).
      cardApprovalAttempt: cardKpi('cardApprovalAttempt', card, cardC, now),
      cardApprovalBuyer: cardKpi('cardApprovalBuyer', card, cardC, now),
      // Pix do curso gerados no período (vencidos inclusive) e quantos foram pagos: a frase do VEN-03.
      pixPaidRate: kv(
        'pixPaidRate',
        rate(pix.paid, pix.generated),
        pixC ? rate(pixC.paid, pixC.generated) : null,
        now,
      ),
      cartsAbandoned: kv('cartsAbandoned', t.abandoned, c?.abandoned ?? null, now),
      revenueFinal: kv('revenueFinal', finalOf(t), c ? finalOf(c) : null, now),
      // Minutos entre gerar o Pix e pagar (Pix do curso pagos no período), com as horas reais.
      pixMedianMin: kv('pixMedianMin', pixMedianMinutes(cur), cmp ? pixMedianMinutes(cmp) : null, now),
    };
    // Antes do 1º aviso do webhook da Kiwify (25/10 às 14:14) o painel ainda não recebia os avisos:
    // só a exportação existe lá (sem abandonos, Pix gerados nem recusas).
    const since = res.webhookSince ?? orders.webhookSince();
    if (since != null) {
      const when = dayTime(since);
      // Período atual começando antes: abandonos, Pix e recusas só contam desde o 1º aviso.
      if (r.from < since)
        for (const id of WEBHOOK_ONLY_KPIS) {
          const v = kpis[id];
          if (v)
            kpis[id] = {
              ...v,
              note: `Conta só desde ${when}: o painel só recebe este dado da Kiwify desde então`,
            };
        }
      if (crx && crx.from < since)
        for (const id of ORDER_KPIS) {
          const v = kpis[id];
          // O "Pendente agora" (estoque) não tem comparação: a nota dele fica.
          if (!v || (stock && id === 'pendingRevenue')) continue;
          kpis[id] = WEBHOOK_ONLY_KPIS.includes(id)
            ? {
                ...v,
                compareValue: null,
                delta: null,
                note: `Sem dado comparável: o painel só recebe este dado da Kiwify desde ${when}`,
              }
            : { ...v, note: `Período anterior só pela exportação da Kiwify (antes de ${when})` };
        }
    }
    // Período mais longo que a base (30D com pedidos só desde 14/10): diz desde quando há dado e, se a
    // comparação cairia antes disso, que não há base de comparação. A nota mais específica fica.
    if (cov != null && (noBase || r.from < cov)) {
      const desde = coverageLabel(cov);
      const note = noBase
        ? `Sem base de comparação: a base só tem pedidos desde ${desde}`
        : `Dados desde ${desde}: antes disso a base não tem pedidos`;
      for (const id of ORDER_KPIS) {
        const v = kpis[id];
        if (v && !v.note && !(stock && id === 'pendingRevenue')) kpis[id] = { ...v, note };
      }
    }
    // Lista cortada mesmo em pedaços: avisa que os números são parciais (nunca soma calada).
    // O "Pendente agora" (estoque) sai de outra consulta (`pendingStock`): a nota dele fica.
    if (res.incomplete || cres?.incomplete)
      for (const id of ORDER_KPIS) {
        const v = kpis[id];
        if (v && !(stock && id === 'pendingRevenue'))
          kpis[id] = { ...v, note: 'Números parciais: o período tem pedidos demais para somar todos' };
      }
    // KPIs dos módulos (VSL, mídia...): só os que têm fonte real; os outros ficam de fora.
    // Os módulos recebem a janela de comparação inteira (a mídia tem a própria cobertura); `c` já vem
    // nulo quando as vendas não têm base.
    const input = { q, now, r, cr, size, t, c };
    const [extra, m] = await Promise.all([
      // Sem os pedidos, um módulo que dependa deles pode falhar: os outros (a Meta) continuam.
      Promise.all(
        modules.map((mod) => {
          const p = mod.kpis?.(input) ?? Promise.resolve({});
          return down ? p.catch(() => ({})) : p;
        }),
      ),
      ctx.loadMedia(r.from, r.to),
    ]);
    for (const k of extra) Object.assign(kpis, k);
    if (down)
      return {
        kpis: markSalesDown(kpis, now, orders.lastEventAt() ?? now),
        t,
        c: null,
        media: m,
        mediaGap: m ? null : mediaGap(ctx.loadMedia, r.from, r.to),
        ordersStaleSince: null,
        ordersDown: true,
      };
    // Leitura de pedidos que falhou e voltou com a última boa: os números de vendas ficam, parados, com
    // o aviso e a hora; os só da Meta seguem atualizados.
    const staleSince = res.staleSince ?? cres?.staleSince ?? null;
    return {
      kpis: staleSince == null ? kpis : markSalesStale(kpis, staleSince, now, salesDownNote(staleSince)),
      t,
      c,
      media: m,
      mediaGap: m ? null : mediaGap(ctx.loadMedia, r.from, r.to),
      ordersStaleSince: staleSince,
    };
  }

  /** Etapas do funil ao vivo (só as com fonte real); sem o funil real, nenhuma (nunca o simulado). */
  async function liveFunnelSteps(q: Query): Promise<FunnelStep[]> {
    const f = moduleOverrides.getFunnel;
    if (!f) return [];
    return f(q)
      .then((r) => r.steps)
      .catch(() => []);
  }

  async function getSnapshot(q: Query): Promise<DashboardSnapshot> {
    const [snap, real, funnel] = await Promise.all([
      provider.getSnapshot(q),
      realKpis(q),
      liveFunnelSteps(q),
    ]);
    const { kpis, t } = real;
    // Só o recorte ao vivo padrão alimenta o stream: um Hoje com filtro ou outra comparação (em outra
    // tela) não pode trocar os números do "Hoje ao vivo".
    const now = clock();
    if (isDefaultLiveQuery(q)) {
      todayKpis = kpis;
      if (real.ordersStaleSince == null) todayKpisAt = now;
    }
    const salesStale = real.ordersStaleSince ?? null;
    const salesDown = real.ordersDown === true;
    const out: DashboardSnapshot = {
      ...snap,
      kpis: { ...snap.kpis, ...kpis },
      // O selo "Kiwify · há…" conta do último aviso real da Kiwify e o da Meta, da última sincronização.
      // VTurb e o rastreador ainda não estão ligados: saem do rodapé em vez de mostrar um "ao vivo" falso.
      freshness: (snap.freshness ?? []).flatMap((f) => {
        // Pedidos da última leitura boa (a consulta falhou): parados desde ela.
        if (f.source === 'kiwify')
          return [
            salesDown
              ? { ...f, fetchedAt: now, dataAsOf: orders.lastEventAt() ?? now, stale: true }
              : salesStale != null
                ? { ...f, fetchedAt: now, dataAsOf: salesStale, stale: true }
                : { ...f, fetchedAt: now, dataAsOf: orders.lastEventAt() ?? now, stale: false },
          ];
        if (f.source === 'meta') return real.media ? metaFreshness(now, real.media, rangeMs(q, now)) : [];
        return [];
      }),
      ...(snap.kpisV2 ? { kpisV2: { ...snap.kpisV2 } } : {}),
      // Funil (TV, etapas): o real do módulo do funil, nunca as etapas da simulação.
      funnel,
      // Pedidos pagos no período por produto (e quantas vendas foram reembolsadas depois).
      headline: salesDown ? SALES_DOWN_NOTE : salesHeadline(t),
    };
    // Os módulos ajustam o snapshot (em paralelo: cada um mexe nos próprios campos). Sem os pedidos, o
    // que depende deles pode falhar sem derrubar a faixa (a Meta continua).
    await Promise.all(
      modules.map((m) => {
        const p = m.patchSnapshot?.(q, out, real);
        return salesDown && p ? p.catch(() => undefined) : p;
      }),
    );
    // Vendas do orgânico e sem origem (V2) pelos pedidos reais, como no `getKpisV2`.
    const k2 = out.kpisV2;
    if (k2 && !salesDown && (k2.organicSales || k2.unattributedShare)) {
      const r = rangeMs(q, now);
      const ch = channelKpis(await load(r.from, r.to), q);
      const ids = (['organicSales', 'unattributedShare'] as const).filter((id) => k2[id]);
      const sim2 = snap.kpisV2 ?? {};
      setKpiV2({ ids }, k2, sim2, 'unattributedShare', ch.unattributedShare, ctx.kiwiFresh(now));
      setKpiV2({ ids }, k2, sim2, 'organicSales', ch.organicSales, ctx.kiwiFresh(now), {
        note: ch.organicNote,
      });
    }
    // Só fica o que foi calculado de verdade (núcleo, `kpis()` e `patchSnapshot` dos módulos): todo
    // KPI que ainda é o objeto da simulação vira "sem valor" com o motivo, nunca o número simulado.
    out.kpis = onlyReal(out.kpis, snap.kpis);
    if (out.kpisV2 && snap.kpisV2) out.kpisV2 = onlyReal(out.kpisV2, snap.kpisV2);
    return out;
  }

  async function getSeries(q: SeriesQuery): Promise<ColumnarSeries> {
    for (const m of modules) {
      const s = await m.series?.(q);
      if (s) return s;
    }
    if (q.key === 'heatmapHourWeekday') {
      // Mapa de calor: vendas pagas do curso (com o filtro da tela) por dia da semana × hora, nos
      // últimos 28 dias (hoje entra). Cada célula é a média por dia: a soma ÷ quantas vezes aquele dia
      // da semana entrou desde o 1º pedido da base (com a base desde 14/10, dividir sempre por 4
      // achatava as médias).
      const now = clock();
      const winStart = dayStart(todayKey(now)) - 27 * DAY;
      const res = await orders.loadFull(winStart, Math.ceil(now / 60_000) * 60_000);
      const rows = res.orders;
      // Servidor sem o início da base: o 1º pedido (qualquer status) da janela marca o começo.
      const first = rows.reduce((m, o) => Math.min(m, o.at), Number.POSITIVE_INFINITY);
      const cov =
        coverageStart(res.dataStartMs ?? null) ??
        orders.dataStart() ??
        (Number.isFinite(first) ? coverageStart(first) : null);
      const start = Math.max(winStart, cov ?? winStart);
      const counts = Array.from({ length: 7 }, () => new Array<number>(24).fill(0));
      for (const o of rows) {
        if (!isPaid(o) || o.kind !== 'front' || !matches(o, q)) continue;
        const d = new Date(o.at - BRT);
        const row = counts[d.getUTCDay()];
        if (row) row[d.getUTCHours()] = (row[d.getUTCHours()] ?? 0) + 1;
      }
      const occ = new Array<number>(7).fill(0);
      for (let d = start; d < now; d += DAY) {
        const wd = new Date(d - BRT).getUTCDay();
        occ[wd] = (occ[wd] ?? 0) + 1;
      }
      const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
      return {
        t: Array.from({ length: 24 }, (_, h) => h * HOUR),
        series: days.map((label, i) => ({
          key: `wd${i}`,
          label,
          role: 'split' as const,
          values: (counts[i] ?? []).map((x) => Math.round((x / Math.max(1, occ[i] ?? 0)) * 10) / 10),
        })),
        // Desde quando a média conta (menos de 4 semanas enquanto a base for nova).
        markers: [{ at: start, kind: 'checkpoint' as const, label: `Média desde ${coverageLabel(start)}` }],
        freshness: freshness(now),
      };
    }
    const real = ['revenueCumulative', 'revenueByHour', 'revenueByDay', 'salesByHour', 'upsellByHour'];
    const byKpi = (q.key === 'kpiByDay' || q.key === 'kpiByHour') && q.kpi != null;
    // Detalhe de uma métrica de pedidos (Vendas, 20X, Faturamento...): dia a dia ou hora a hora reais.
    const orderPick = byKpi && q.kpi ? ORDER_KPI_PICK[q.kpi] : undefined;
    const ratio = byKpi && q.kpi ? RATIO_SERIES[q.kpi] : undefined;
    if (ratio) return ratioSeries(q, ratio);
    // Outra métrica real (sem série própria): o minigráfico do próprio número; sem fonte real, vazio.
    if (byKpi && !orderPick) return kpiSparkSeries(q);
    // Gráfico sem conta real (nem aqui nem nos módulos): vazio, nunca a série da simulação.
    if (!real.includes(q.key) && !orderPick) return emptySeries();
    const now = clock();
    const r = rangeMs(q, now);
    const cr0 = compareMs(q, now);
    // Balde de 5 min só num dia (o acumulado do herói); em períodos maiores, hora.
    const size =
      q.key === 'kpiByDay' || q.bucket === '1d'
        ? DAY
        : q.bucket === '1w'
          ? 7 * DAY
          : q.bucket === '5m' && r.end - r.from <= DAY + HOUR
            ? 5 * 60_000
            : HOUR;
    // A linha de comparação mostra o período anterior inteiro (ex.: ontem até a meia-noite).
    const [rows, crows] = await Promise.all([
      load(r.from, r.to),
      cr0 ? load(cr0.from, cr0.end) : Promise.resolve(null),
    ]);
    // Antes do 1º pedido da base: baldes vazios; comparação que começa antes disso não aparece.
    const cov = orders.dataStart();
    const cr = cr0 && (cov == null || cr0.from >= cov) ? cr0 : null;
    const cEnd = cr ? cr.end : 0;
    // Vendas aprovadas (pagas e reembolsadas depois): o faturamento e as contagens de vendas contam todas
    // (a regra da tela Vendas); as reembolsadas também saem à parte na série das vendas.
    const cur = rows.filter((o) => isSold(o) && matches(o, q));
    const cmp = crows?.filter((o) => isSold(o) && matches(o, q)) ?? null;
    const main = (pick: (o: RealOrderRow) => number) => buckets(cur, r.from, r.end, size, now, pick, cov);
    // Mês passado mais curto (31/10 → 30/09): os baldes depois do fim dele ficam vazios.
    const prev = (pick: (o: RealOrderRow) => number) =>
      cmp && cr ? buckets(cmp, cr.from, cEnd, size, cr.end - 1, pick).values : null;
    const gross = revenueOf;
    const front = (o: RealOrderRow) => (o.kind === 'front' ? 1 : 0);
    const upsell = (o: RealOrderRow) => (o.kind === 'upsell' ? 1 : 0);
    const refundedFront = (o: RealOrderRow) => (o.kind === 'front' && isRefundLike(o.status) ? 1 : 0);
    const m = main(gross);
    const out: ColumnarSeries = { t: m.t, series: [], markers: [], freshness: freshness(now) };
    const add = (
      key: string,
      label: string,
      role: ColumnarSeries['series'][number]['role'],
      values: (number | null)[] | null,
    ) => {
      if (values) out.series.push({ key, label, role, values });
    };
    if (orderPick) {
      add(q.kpi as string, 'No período', 'primary', main(orderPick).values);
      add(`${q.kpi as string}_cmp`, 'Comparação', 'compare', prev(orderPick));
    } else if (q.key === 'revenueCumulative') {
      // Acumulado dentro do dia: cada ponto é o total até o FIM do balde (00:00 = R$ 0; 14:00 = até
      // 14:00), como a simulação entrega.
      if (size < DAY) out.t = m.t.map((b) => b + size);
      add('revenue', 'Faturamento', 'primary', cumulative(m.values));
      add(
        'revenue_cmp',
        'Comparação',
        'compare',
        (() => {
          const v = prev(gross);
          return v ? cumulative(v) : null;
        })(),
      );
    } else if (q.key === 'revenueByHour' || q.key === 'revenueByDay') {
      add('revenue', 'Faturamento', 'primary', m.values);
      add('revenue_cmp', 'Comparação', 'compare', prev(gross));
    } else if (q.key === 'salesByHour') {
      add('sales', 'Vendas do curso', 'primary', main(front).values);
      add('upsell', 'Vendas do 20X', 'split', main(upsell).values);
      // Das vendas do curso acima, as reembolsadas depois (já contadas nelas): "N reembolsadas".
      add('refunded', 'Reembolsadas', 'split', main(refundedFront).values);
      add('sales_cmp', 'Comparação', 'compare', prev(front));
    } else {
      add('upsell', 'Vendas do 20X', 'primary', main(upsell).values);
    }
    return out;
  }

  /** Série de uma razão (20X ÷ curso, ticket): acumulada por hora num dia; por dia, a de cada dia. */
  async function ratioSeries(
    q: SeriesQuery,
    def: NonNullable<(typeof RATIO_SERIES)[KpiId]>,
  ): Promise<ColumnarSeries> {
    const now = clock();
    const r = rangeMs(q, now);
    const cr = compareMs(q, now);
    const daily = q.key === 'kpiByDay';
    const size = daily ? DAY : HOUR;
    const [rows, crows0] = await Promise.all([
      load(r.from, r.to),
      cr ? load(cr.from, cr.end) : Promise.resolve(null),
    ]);
    // Comparação que começa antes do 1º pedido da base: sem linha (nunca contra zero).
    const cov = orders.dataStart();
    const crows = cr && (cov == null || cr.from >= cov) ? crows0 : null;
    const cEnd = cr ? cr.end : 0;
    const make = (list: RealOrderRow[], from: number, to: number, upTo: number) => {
      const own = list.filter((o) => matches(o, q));
      const num = buckets(own, from, to, size, upTo, def.num, cov);
      const den = buckets(own, from, to, size, upTo, def.den, cov).values;
      const v = daily ? ratioOf(num.values, den) : ratioOf(cumulative(num.values), cumulative(den));
      return { t: num.t, values: def.cents ? v.map((x) => (x == null ? null : Math.round(x))) : v };
    };
    const main = make(rows, r.from, r.end, now);
    const out: ColumnarSeries = { t: main.t, series: [], markers: [], freshness: freshness(now) };
    const id = q.kpi as string;
    out.series.push({ key: id, label: 'No período', role: 'primary', values: main.values });
    if (crows && cr)
      out.series.push({
        key: `${id}_cmp`,
        label: 'Comparação',
        role: 'compare',
        values: make(crows, cr.from, cEnd, cr.end - 1).values,
      });
    return out;
  }

  /**
   * Série de um KPI real sem conta própria por hora/dia: o minigráfico que o número já traz (mesmos
   * baldes). KPI sem fonte real, ou balde diferente do minigráfico: eixo sem linha (nunca a simulação).
   */
  async function kpiSparkSeries(q: SeriesQuery): Promise<ColumnarSeries> {
    const now = clock();
    const r = rangeMs(q, now);
    const size = q.key === 'kpiByDay' ? DAY : HOUR;
    const t = buckets([], r.from, r.end, size, now, () => 0).t;
    const out: ColumnarSeries = { t, series: [], markers: [], freshness: [] };
    if (!q.kpi || !realKpiIds.has(q.kpi) || size !== sparkSize(r)) return out;
    const v = (await realKpis(q)).kpis[q.kpi];
    if (!v?.spark || v.spark.length !== t.length) return out;
    out.freshness = v.freshness ?? [];
    out.series.push({ key: q.kpi, label: 'No período', role: 'primary', values: v.spark });
    if (v.sparkCompare?.length === t.length)
      out.series.push({ key: `${q.kpi}_cmp`, label: 'Comparação', role: 'compare', values: v.sparkCompare });
    return out;
  }

  async function listReal<K extends ListKind>(kind: K, q: ListQuery): Promise<Page<unknown>> {
    const now = clock();
    const r = rangeMs(q, now);
    const rows = (await load(r.from, r.to)).filter((o) => matches(o, q));
    if (kind === 'refunds') {
      // Reembolsos feitos no período (pela data do reembolso), com a data real da compra.
      const fromServer = await orders.loadRefunds(r.from, r.to);
      const source = fromServer
        ? fromServer.filter((o) => matches(o, q))
        : rows.filter((o) => isRefundLike(o.status));
      const refunds: Refund[] = [];
      for (const o of source) {
        const ord = toOrder(o);
        if (!ord || (ord.status !== 'refunded' && ord.status !== 'chargedback')) continue;
        const purchasedAt = ord.approvedAt ?? ord.createdAt;
        const refundedAt = ord.refundedAt ?? o.at;
        refunds.push({
          id: `rf_${o.id}`,
          orderId: o.id,
          kind: ord.status === 'chargedback' ? 'chargeback' : 'refund',
          // No valor do produto, como o faturamento.
          amount: revenueOf(o),
          product: ord.product,
          paymentMethod: ord.paymentMethod,
          purchasedAt,
          refundedAt,
          daysAfterPurchase: Math.max(0, Math.floor((refundedAt - purchasedAt) / DAY)),
          reason: null,
          attribution: ord.attribution,
        });
      }
      refunds.sort((a, b) => b.refundedAt - a.refundedAt);
      return paginate(refunds, q);
    }
    // Carrinho abandonado só abre no detalhe (pelo id): a lista de pedidos não o mostra.
    let list: Order[] = ordersOf(rows).filter((o) => !o.abandoned);
    // 20X ligado ao curso da mesma pessoa: a quebra do VEN-04 usa o pagamento e a origem do curso.
    linkUpsellParents(list);
    if (q.status?.length) list = list.filter((o) => q.status?.includes(o.status));
    if (q.types?.length)
      list = list.filter((o) => q.types?.includes(o.product) || q.types?.includes(o.paymentMethod));
    if (q.search) {
      const s = q.search.toLowerCase();
      list = list.filter((o) => o.ref.toLowerCase().includes(s) || o.productName.toLowerCase().includes(s));
    }
    if (q.sort?.key === 'amount')
      list.sort((a, b) => (a.amounts.gross - b.amounts.gross) * (q.sort?.dir === 'asc' ? 1 : -1));
    else list.sort((a, b) => (b.approvedAt ?? b.createdAt) - (a.approvedAt ?? a.createdAt));
    return paginate(list, q);
  }

  async function listActivity(q: ListQuery): Promise<Page<unknown>> {
    const now = clock();
    const r = rangeMs(q, now);
    const [all, refunds] = await Promise.all([
      load(r.from, r.to).catch(() => [] as RealOrderRow[]),
      // Reembolsos pela data do reembolso (um de hoje sobre compra antiga aparece hoje).
      orders.loadRefunds(r.from, r.to).catch(() => null),
    ]);
    const rows = all.filter((o) => matches(o, q));
    return paginate(realActivity(rows, refunds?.filter((o) => matches(o, q)) ?? null), q);
  }

  async function getKpisV2(
    q: Parameters<NonNullable<PainelProviderV2['getKpisV2']>>[0],
  ): Promise<Partial<Record<KpiIdV2, KpiValueV2>>> {
    const sim = await (v2.getKpisV2 as NonNullable<PainelProviderV2['getKpisV2']>)(q);
    const out = { ...sim };
    const now = clock();
    const r = rangeMs(q, now);
    const ch = channelKpis(await load(r.from, r.to), q);
    const fresh = ctx.kiwiFresh(now);
    setKpiV2(q, out, sim, 'unattributedShare', ch.unattributedShare, fresh);
    setKpiV2(q, out, sim, 'organicSales', ch.organicSales, fresh, { note: ch.organicNote });
    await Promise.all(modules.map((m) => m.patchKpisV2?.(q, out, sim)));
    // Só fica o que foi calculado de verdade; o que ainda é o objeto da simulação sai sem valor.
    return onlyReal(out, sim);
  }

  /**
   * Detalhe de um KPI (sheet da métrica): os reais saem das vendas, da Meta e dos módulos; os que só o
   * snapshot ao vivo calcula (recuperação, régua...), do snapshot; sem fonte real, sem valor (nunca o
   * detalhe da simulação).
   */
  async function getKpiDetail(q: Parameters<DataProvider['getKpiDetail']>[0]) {
    for (const m of modules) {
      const d = await m.kpiDetail?.(q);
      if (d) return d;
    }
    const v =
      (realKpiIds.has(q.kpi) ? (await realKpis(q)).kpis[q.kpi] : undefined) ??
      (await getSnapshot(q)).kpis[q.kpi] ??
      noRealKpi(q.kpi, NO_REAL_SOURCE);
    const now = clock();
    const r = rangeMs(q, now);
    const axis = buckets([], r.from, r.end, sparkSize(r), now, () => 0).t;
    const values = v.spark && v.spark.length === axis.length ? v.spark : axis.map(() => null);
    return {
      value: v,
      series: {
        t: axis,
        series: [{ key: q.kpi, label: q.kpi, role: 'primary' as const, values }],
        markers: [],
        freshness: v.freshness ?? freshness(now),
      },
      byCampaign: [],
    };
  }

  // Pedidos, decisões e atividades da simulação viram os números reais de hoje (mesmo seq): o feed e o
  // "Agora" leem as listas reais.
  /**
   * Números de hoje para o stream. Sem atualizar há mais de 2 min (as leituras estão falhando): os de
   * vendas saem marcados como parados, com a hora da última leitura boa.
   */
  function liveKpis(): Partial<Record<KpiId, KpiValue>> {
    const k = todayKpis ?? {};
    const now = clock();
    if (todayKpisAt == null || now - todayKpisAt <= LIVE_KPIS_STALE_MS) return k;
    return markSalesStale(k, todayKpisAt, now, salesDownNote(todayKpisAt));
  }

  function rewrite(m: LiveMessage): LiveMessage {
    const real = liveKpis();
    const patch = (kpis: Partial<Record<KpiId, KpiValue>>): LiveMessage => ({
      v: m.v,
      seq: m.seq,
      ts: m.ts,
      type: 'kpi.patch',
      topic: 'kpis',
      data: { kpis },
    });
    // Nenhum número da simulação passa pelo stream: o patch leva só os KPIs reais de hoje (sem a meta
    // e a frase simuladas); o resto continua como o snapshot ao vivo deixou.
    if (m.type === 'kpi.patch') return patch(real);
    if (m.type === 'insights.refreshed') return patch(real);
    if (m.type === 'vsl.live') {
      // "Assistindo agora" da VTurb (sessões ativas nos últimos 5 min) no lugar do número simulado.
      const last = ctx.lastVsl();
      if (last?.main && last.live != null)
        return {
          ...m,
          data: {
            playerId: last.main.playerId,
            viewers: last.live,
            pitchReachedToday: last.main.stats.overPitch,
          },
        };
      return patch(real);
    }
    // Situação simulada de uma fonte vira a última real dela (Kiwify, Meta, VTurb); sem a real (ou o
    // rastreador), não chega ao rodapé: vira um patch sem mudança para não abrir buraco no seq.
    if (m.type === 'source.status') {
      const live =
        m.data.source === 'kiwify'
          ? kiwifyLive
          : m.data.source === 'meta'
            ? metaLive
            : m.data.source === 'vturb'
              ? vturbLive
              : null;
      return live ? { ...m, data: live } : patch(real);
    }
    // "Refaça tudo" (virada do dia, buraco no seq) só manda reler: as leituras já são as reais.
    if (m.type === 'resync') return m;
    // Todo o resto que vem da simulação (pedido, decisão, atividade, recuperação, evento do rastreador,
    // briefing, mudança de campanha...) não entra nas listas: vira o patch dos números reais (mesmo
    // seq, sem buraco). Os dados reais dessas listas chegam pelas leituras e pelos módulos.
    return patch(real);
  }

  function subscribe(...args: Parameters<DataProvider['subscribe']>): ReturnType<DataProvider['subscribe']> {
    const [topics, onMessage, opts] = args;
    let lastSeq = 0;
    let lastV = 1 as LiveMessage['v'];
    let lastMetaSync: number | null = null;
    const emit = (m: { type: LiveMessage['type']; topic: LiveMessage['topic']; data: unknown }) =>
      onMessage({ v: lastV, seq: lastSeq, ts: clock(), ...m } as LiveMessage);
    /** Atualizações seguidas em que a leitura dos pedidos falhou (zera quando uma dá certo). */
    let kiwifyFails = 0;
    // Atualiza os números de hoje e a situação real das fontes a cada 30 s, enquanto a tela estiver aberta.
    const refresh = async () => {
      const today = todayKey(clock());
      let r: Awaited<ReturnType<typeof realKpis>> | null = null;
      try {
        r = await realKpis({
          range: { preset: 'today', from: today, to: today },
          compare: 'yesterday_same_time',
          filters: {},
        });
      } catch {
        r = null;
      }
      const now = clock();
      // A leitura dos pedidos falhou nesta atualização (mesmo servida pela última leitura boa)?
      const failed = r == null || r.ordersStaleSince != null || orders.health().fails > 0;
      kiwifyFails = failed ? kiwifyFails + 1 : 0;
      if (r) {
        todayKpis = r.kpis;
        if (r.ordersStaleSince == null) todayKpisAt = now;
        // Gasto novo da Meta (o servidor busca o dia de hoje a cada 2 min): as tabelas e os gráficos de
        // tráfego se refazem sozinhos e os números do dia chegam no mesmo patch.
        const at = r.media?.lastSyncAt ?? null;
        if (at != null && lastMetaSync != null && at !== lastMetaSync) {
          for (const m of modules) m.onMediaSync?.();
          emit({ type: 'kpi.patch', topic: 'kpis', data: { kpis: r.kpis } });
          for (const level of ['account', 'campaign', 'adset', 'ad'] as const)
            emit({ type: 'insights.refreshed', topic: 'insights', data: { level, at } });
          // Gráficos de gasto e KPIs da tela refazem também (mesmo efeito do "Tentar de novo").
          emit({ type: 'resync', topic: 'kpis', data: { reason: 'gap' } });
        }
        if (at != null) lastMetaSync = at;
        // Números de vendas parados (a leitura falhou): o patch leva o aviso na hora.
        if (r.ordersStaleSince != null) emit({ type: 'kpi.patch', topic: 'kpis', data: { kpis: r.kpis } });
      }
      // Situação real das fontes na hora (mesmo seq da última mensagem: sem buraco). A Meta e a VTurb
      // andam com o relógio (antes ficavam paradas na da abertura da página). Sem os números de hoje (a
      // leitura toda falhou), a Meta é lida à parte: a falha dos pedidos não vira "Meta com erro".
      const hourAgo = now - HOUR;
      const [kst, v, m1] = await Promise.all([
        kiwifyStatus(kiwifyFails >= KIWIFY_FAILS_FOR_ERROR).catch(() => null),
        ctx.loadVsl(todayStart(), now).catch(() => null),
        r ? Promise.resolve(r.media) : ctx.loadMedia(hourAgo, now).catch(() => null),
      ]);
      if (kst) {
        // Erro: a hora do último aviso real fica (nunca a da simulação).
        kiwifyLive = kst;
        emit({ type: 'source.status', topic: 'sources', data: kst });
      }
      const metaBase = metaLive ?? ({ source: 'meta', latency: 'near_realtime' } as IntegrationStatus);
      const gap = r ? (r.mediaGap ?? null) : m1 ? null : mediaGap(ctx.loadMedia, hourAgo, now);
      const mst = realMeta(metaBase, m1, gap, now);
      emit({ type: 'source.status', topic: 'sources', data: mst });
      emit({ type: 'source.status', topic: 'sources', data: realVturb(v, vturbLive ?? undefined, now) });
    };
    // Venda real nova (Kiwify) vira `order.upsert` (toast e som de venda); a primeira leitura só marca o
    // que já existia, para não tocar o som de vendas antigas ao abrir o painel.
    let seen: Set<string> | null = null;
    const newSales = () => {
      const now = clock();
      const from = Math.floor((now - 2 * HOUR) / 60_000) * 60_000;
      void fetchOrders(from, Math.ceil(now / 60_000) * 60_000)
        .then(({ orders: list }) => {
          const paid = list.filter((o) => o.status === 'paid');
          if (seen == null) {
            seen = new Set(paid.map((o) => o.id));
            return;
          }
          // 20X novo já ligado ao curso da mesma pessoa (está quase sempre nestas 2 h), como na lista:
          // o VEN-04 usa o pagamento e a origem do curso desde o primeiro aviso.
          const all = ordersOf(list);
          linkUpsellParents(all);
          const byId = new Map(all.map((o) => [o.id, o]));
          for (const o of paid.sort((a, b) => a.at - b.at)) {
            if (seen.has(o.id)) continue;
            seen.add(o.id);
            const order = byId.get(o.id);
            if (!order) continue;
            orders.invalidate();
            emit({ type: 'order.upsert', topic: 'orders', data: order });
          }
        })
        .catch(() => undefined);
    };
    const tick = () => void refresh().catch(() => undefined);
    tick();
    newSales();
    const salesTimer = setInterval(newSales, 30_000);
    const timer = setInterval(tick, 30_000);
    // Volta da aba em segundo plano (ou do celular bloqueado) depois de mais de 1 min: relê tudo na hora
    // (pedidos, Meta, VTurb, listas) em vez de esperar o próximo intervalo.
    let hiddenAt: number | null = null;
    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        hiddenAt = clock();
        return;
      }
      const away = hiddenAt == null ? 0 : clock() - hiddenAt;
      hiddenAt = null;
      if (away <= BACK_FROM_HIDDEN_MS) return;
      orders.invalidate();
      ctx.invalidateMedia();
      lostCache = null;
      for (const m of modules) m.onMediaSync?.();
      tick();
      newSales();
      emit({ type: 'resync', topic: 'kpis', data: { reason: 'gap' } });
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    const unsubModules = modules.map((m) => m.subscribe?.(emit));
    const unsub = provider.subscribe(
      topics,
      (m) => {
        lastSeq = Math.max(lastSeq, m.seq);
        lastV = m.v;
        onMessage(rewrite(m));
      },
      opts,
    );
    return () => {
      clearInterval(timer);
      clearInterval(salesTimer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      for (const u of unsubModules) u?.();
      unsub();
    };
  }

  const patchBoot = async <B extends { settings: Settings }>(boot: B): Promise<B> => {
    let b = boot;
    for (const m of modules) if (m.patchBoot) b = await m.patchBoot(b);
    return b;
  };

  const moduleOverrides = Object.assign(
    {},
    ...modules.map((m) => m.overrides ?? {}),
  ) as Partial<DataProvider>;

  const webhookInfo: KiwifyWebhookInfo = {
    // Já visto numa consulta; senão pergunta ao servidor com os pedidos de hoje (o servidor manda junto).
    kiwifyWebhookSince: async () => {
      const seen = orders.webhookSince();
      if (seen != null) return seen;
      const now = clock();
      const res = await orders.loadFull(todayStart(), Math.ceil(now / 60_000) * 60_000);
      return res.webhookSince ?? null;
    },
  };

  const overrides: Partial<DataProvider> & KiwifyWebhookInfo = {
    ...moduleOverrides,
    ...webhookInfo,
    init: async (...a: Parameters<DataProvider['init']>) => {
      orders.prefetch();
      const boot = await patchBoot(await provider.init(...a));
      return { ...boot, integrations: await withRealKiwify(boot.integrations) };
    },
    switchViewer: async (...a: Parameters<DataProvider['switchViewer']>) => {
      const boot = await patchBoot(await provider.switchViewer(...a));
      return { ...boot, integrations: await withRealKiwify(boot.integrations) };
    },
    status: async () => withRealKiwify(await provider.status()),
    getSnapshot,
    getSeries,
    subscribe,
    ...(v2.getKpisV2 ? { getKpisV2 } : {}),
    // Esteira 1-3-1: os testes ainda não têm marcação real; vazia em vez de inventada.
    ...(v2.getTestLane ? { getTestLane: async (): Promise<TestLaneItem[]> => [] } : {}),
    // Fatias e LTV por anúncio dependem do padrão de nome e do backend: vazios até existirem de verdade.
    ...(v2.getSlices ? { getSlices: async () => [] } : {}),
    ...(v2.getLtv ? { getLtv: async () => [] } : {}),
    getKpiDetail,
    // Nome, e-mail e telefone do cliente não ficam no painel: estão no pedido, na Kiwify.
    revealPii: async () => {
      throw Object.assign(new Error('Os dados do cliente ficam no pedido, na Kiwify.'), {
        code: 'forbidden',
      });
    },
    list: (async (kind: ListKind, q: ListQuery) => {
      for (const m of modules) {
        const page = await m.list?.(kind, q);
        if (page) return page;
      }
      if (kind === 'orders' || kind === 'refunds') return listReal(kind, q);
      if (kind === 'activity') return listActivity(q);
      return provider.list(kind, q);
    }) as DataProvider['list'],
  };
  return new Proxy(provider, {
    get(target, prop, receiver) {
      if (typeof prop === 'string' && prop in overrides) return overrides[prop as keyof typeof overrides];
      const v = Reflect.get(target, prop, receiver) as unknown;
      return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(target) : v;
    },
  });
}
